import { z } from 'zod';
import { AddressSchema } from './address';
import { AppraiserAssignmentSchema } from './appraiser-assignment';
import { ISODateSchema, ISODateTimeSchema, UUIDSchema } from './common';
import { DocumentSchema, ImageSchema } from './file';
import { MandatorSchema } from './mandator';
import { ValuationStatusSchema } from './valuation-primitives';
export const OrderStatusSchema = z.enum([
    'open',
    'accepted',
    'in_communication_with_owner',
    'inspection_scheduled',
    'in_progress',
    'in_review',
    'done',
    'temp_hide_in_portal',
    'canceled',
    'on_hold',
    'blocked',
]);
const createAssetUsageTypeSchema = (usageType) => z.object({ usage_type: z.literal(usageType), sub_usage_type: z.string().optional() });
export const RetailAssetUsageTypeSchema = createAssetUsageTypeSchema('retail');
export const OfficeAssetUsageTypeSchema = createAssetUsageTypeSchema('office');
export const ResidentialAssetUsageTypeSchema = createAssetUsageTypeSchema('residential');
export const MixedUseUsageTypeSchema = createAssetUsageTypeSchema('mixed');
export const GarageUsageTypeSchema = createAssetUsageTypeSchema('garage');
export const OtherUsageTypeSchema = createAssetUsageTypeSchema('other');
export const UnknownUsageTypeSchema = z.object({
    usage_type: z.literal('unknown'),
    sub_usage_type: z.string().optional(),
});
export const AssetUsageTypeSchema = z.union([
    RetailAssetUsageTypeSchema,
    OfficeAssetUsageTypeSchema,
    ResidentialAssetUsageTypeSchema,
    MixedUseUsageTypeSchema,
    GarageUsageTypeSchema,
    UnknownUsageTypeSchema,
    OtherUsageTypeSchema,
]);
export const ContactSchema = z.object({
    salutation: z.string().optional(),
    name: z.string().optional(),
    job_title: z.string().optional(),
    company: z.string().optional(),
    street: z.string().optional(),
    house_number: z.string().optional(),
    country: z.string().optional(),
    zip: z.string().optional(),
    city: z.string().optional(),
    email: z.string().optional(),
    phone_number: z.string().optional(),
    note: z.string().optional(),
});
export const ReasonForValuationSchema = z.enum([
    'transaction',
    'recurring_valuation',
    'division_of_wealth',
    'statement_of_assets',
    'financing_purposes',
    'court_proceedings',
    'purchase',
    'sale',
    'tax_purposes',
    'partial_sale',
    'accounting_purposes',
    'intrinsic_value_check',
    'auction',
    'lending_purposes',
]);
export const AssetStatusSchema = z.enum(['in_planning', 'under_construction', 'existing', 'time_of_projection', 'after_finalization']);
export const NewOrderSchema = z.object({
    address: AddressSchema,
    valuation_reason: ReasonForValuationSchema,
    customer_uuid: UUIDSchema,
    cost_center_uuid: UUIDSchema.optional(),
    asset_uuid: UUIDSchema.optional(),
    valuation_date: ISODateSchema.optional(),
    inspection_date: ISODateSchema.optional(),
    due_date: ISODateSchema.optional(),
    order_date: ISODateSchema.optional(),
    asset_state: AssetStatusSchema.optional(),
    asset_usage_types: z.array(AssetUsageTypeSchema).optional(),
    contacts: z.array(ContactSchema),
    mandators: z.array(MandatorSchema),
    customer_asset_reference: z.string().optional(),
    note: z.string().optional(),
    assigned_customer_users: z.array(z.number()),
    appraiser_assignment: AppraiserAssignmentSchema.optional(),
});
export const ExternalParticipantMailboxSchema = z.object({
    proxy_email: z.string(),
});
const AssetTitleImageSchema = ImageSchema.omit({ is_included_into_the_report: true });
export const ExistingOrderSchema = NewOrderSchema.extend({
    uuid: UUIDSchema,
    images: z.array(ImageSchema),
    documents: z.array(DocumentSchema),
    status: OrderStatusSchema,
    status_message: z.string().optional(),
    created_by: z.number(),
    created_on: ISODateTimeSchema,
    updated_on: ISODateTimeSchema.optional(),
    service_provider: UUIDSchema,
    observers: z.array(UUIDSchema),
    asset_title_image: AssetTitleImageSchema.optional(),
    external_participant_mailbox: ExternalParticipantMailboxSchema.optional(),
});
export const OrderListSchema = z.object({
    orders: z.array(ExistingOrderSchema),
});
export const NewExternalParticipantSchema = z.object({
    last_name: z.string(),
    salutation: z.string(),
    email: z.string(),
    first_name: z.string().optional(),
    organization_name: z.string().optional(),
});
export const ExternalParticipantSchema = z.object({
    uuid: UUIDSchema,
    user_uuid: UUIDSchema.optional(),
    order_uuid: UUIDSchema,
    email: z.string(),
    salutation: z.string(),
    last_name: z.string(),
    first_name: z.string().optional(),
    organization_name: z.string().optional(),
    removed_at: ISODateTimeSchema.optional(),
});
export const ExternalParticipantWithProxyEmailSchema = ExternalParticipantSchema.extend({
    proxy_email: z.string(),
});
export const FailedOutboundDeliverySchema = z.object({
    message_uuid: UUIDSchema,
    recipient_email: z.string(),
    failed_at: ISODateTimeSchema.optional(),
    failure_reason: z.string().optional(),
    last_error: z.string().optional(),
});
export const OutboundThreadStatusSchema = z.object({
    thread_uuid: UUIDSchema,
    is_blocked: z.boolean(),
    failure_reason: z.string().optional(),
    failed_delivery: FailedOutboundDeliverySchema.optional(),
});
export const ExternalParticipantListSchema = z.object({
    participants: z.array(ExternalParticipantSchema),
    proxy_email: z.string().optional(),
    outbound_thread_status: OutboundThreadStatusSchema.optional(),
});
export const OrderSummaryBaseSchema = z.object({
    uuid: UUIDSchema,
    customer_uuid: UUIDSchema,
    asset_uuid: UUIDSchema.optional(),
    created_by: z.number(),
    status: OrderStatusSchema,
    valuation_date: ISODateSchema.optional(),
    inspection_date: ISODateSchema.optional(),
    due_date: ISODateSchema.optional(),
    order_date: ISODateSchema.optional(),
    customer_asset_reference: z.string().optional(),
    address: AddressSchema,
    assigned_customer_users: z.array(z.number()),
    mandators: z.array(MandatorSchema),
    observers: z.array(UUIDSchema),
    appraiser_assignment: AppraiserAssignmentSchema.optional(),
    asset_title_image: ImageSchema.optional(),
    valuation_reason: z.string().optional(),
    service_provider: UUIDSchema.optional(),
    external_participant_mailbox: ExternalParticipantMailboxSchema.optional(),
});
export const OrderWithPlausibilitySummarySchema = z.object({
    order: OrderSummaryBaseSchema,
    latest_draft_uuid: UUIDSchema.optional(),
    latest_draft_title: z.string().optional(),
    valuation_status: ValuationStatusSchema.optional(),
    is_handed_over: z.boolean().optional(),
    is_approved: z.boolean().optional(),
    is_internally_approved: z.boolean().optional(),
    annotation_count: z.number(),
    plausibility_uuid: UUIDSchema.optional(),
    participant_ids: z.array(z.number()),
});
export const OrderWithPlausibilitySummaryListSchema = z.object({
    orders: z.array(OrderWithPlausibilitySummarySchema),
});
